'use client'

import { useState, useCallback } from 'react'
import { MutationCache, QueryClient } from '@tanstack/react-query'
import { ErrorToast, toErrorMessage } from '@/components/ui/ErrorToast'
import { markSurfaced } from '@/lib/surfaced-error'

/**
 * One place that guarantees a failed mutation says something to the merchant.
 *
 * Every tRPC mutation failure lands here, so a page no longer has to remember
 * to wire up its own error UI — forgetting was the bug: on the products page a
 * merchant whose trial had ended clicked "Assign", the server returned a clear
 * FORBIDDEN explaining exactly what to do, and absolutely nothing appeared on
 * screen. The only place it showed up was the crash reporter.
 *
 * Pages that DO define their own `onError` keep it and are skipped here, so the
 * five that already render their own ErrorToast are untouched and nobody sees
 * two toasts for one failure.
 */
export function useMutationErrorQueryClient(): [QueryClient, React.ReactNode] {
  const [message, setMessage] = useState<string | null>(null)
  const dismiss = useCallback(() => setMessage(null), [])

  const [queryClient] = useState(
    () =>
      new QueryClient({
        mutationCache: new MutationCache({
          onError: (error, _vars, _ctx, mutation) => {
            // Always mark: whether this handler shows it or the mutation's own
            // onError does, the merchant is being told, so it isn't a crash.
            markSurfaced(error)
            // Defer to a mutation that handles its own errors.
            if (mutation.options.onError) return
            setMessage(toErrorMessage(error))
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
            // Surface failures instead of rendering a permanent empty state:
            // when a query fails with no cached data to show, throw to the
            // nearest error boundary (app-embed-shopify/(app)/error.tsx).
            // Background refetch failures with data on screen stay silent.
            throwOnError: (_error, query) => query.state.data === undefined,
          },
        },
      })
  )

  return [queryClient, <ErrorToast key="global-mutation-error" message={message} onDismiss={dismiss} />]
}
